/**
 * 파일명 : evl0108m.tsx
 * 설명   : 평가정보 관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0       2026-01-05   이예원     최초작성
 */

/************************************************
 * Import 영역
 ************************************************/

import React, { useState, useMemo, useEffect } from "react";
import { Button, Typography, message, Select, Alert, Row, Col, InputNumber, Progress, Divider, Input, Modal, DatePicker, Space, Tabs, Table } from "antd";
import type { TabsProps, TableColumnsType } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Evl0108mAction } from '../../../redux/slice/evl/evl0108m_slice';
import { IEvlCycleState, IYearOptionState, IAuthorizedUserState, IAllEmployeeState } from "redux/models/evl/evl0108m_model";

import * as common from 'libs/common';
import * as commonProxy from 'libs/CommonProxy';

const { Text } = Typography;

// ============================================================
// 메인 컴포넌트
// ============================================================

/**
 * 컴포넌트명 : Evl0108m
 * 설명 : 인사평가 설정 관리 화면 메인 컴포넌트
 * 주요 기능:
 *  - 평가년도별 평가 주기 관리 (인사평가 시작일, 자기평가/프로젝트평가/1차/2차평가 마감일)
 *  - 업무서약서 작성 기간 설정 (시작일, 종료일)
 *  - 등급별 분포 비율 설정 (S, A, B, C, D 등급별 상위 비율)
 * 권한:
 *  - 관리자만 접근 가능
 *  - 지난 년도 데이터는 조회만 가능하고 수정 불가
 */
const Evl0108m: React.FC = () => {
  // ============================================================
  // Redux 상태 관리
  // ============================================================

  /**
   * dispatch : Redux action을 실행하기 위한 함수
   * 사용처: 평가 주기 데이터를 Redux store에 저장할 때 사용
   */
  const dispatch = useAppDispatch();

  /**
   * Redux store에서 상태 가져오기
   */
  const evlCycleList = useAppSelector((state) => state.Evl0108m.evlCycle);
  const availableYears = useAppSelector((state) => state.Evl0108m.availableYears);
  const inquiryCnds = useAppSelector((state) => state.Evl0108m.inquiryCnds);

  // ============================================================
  // 로컬 상태 - Redux에서 가져온 데이터를 임시로 관리
  // ============================================================

  /**
   * evaluationYear : 현재 선택된 평가년도 (Redux의 inquiryCnds.evlYear 기반)
   * 초기값: Redux에서 가져오거나 현재 년도 사용
   */
  const evaluationYear = inquiryCnds.evlYear || new Date().getFullYear().toString();

  /**
   * evlCycle : 평가주기 마스터 데이터 (Redux의 evlCycleList[0] 기반)
   * 로컬 상태로 관리하여 수정 시 즉시 반영
   */
  const [evlCycle, setEvlCycle] = useState<IEvlCycleState>({
    evlYear    : evaluationYear,
    hrEvlStDt  : "",
    slfEvlEdDt : "",
    pjtEvlEdDt : "",
    eval1EdDt  : "",
    eval2EdDt  : "",
    bizPlgStDt : "",
    bizPlgEdDt : "",
  });

  // ============================================================
  // UI 전용 로컬 상태
  // ============================================================

  /**
   * hasUnsavedChanges : 저장되지 않은 변경사항이 있는지 여부
   */
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * deadlineEditModalVisible : 평가 마감일 수정 모달의 표시 여부
   */
  const [deadlineEditModalVisible, setDeadlineEditModalVisible] = useState(false);

  /**
   * editingEvaluationType : 현재 수정 중인 평가 유형
   */
  const [editingEvaluationType, setEditingEvaluationType] = useState<keyof IEvlCycleState | null>(null);

  /**
   * newDeadlineDate : 모달에서 선택한 새로운 마감일
   */
  const [newDeadlineDate, setNewDeadlineDate] = useState<Dayjs | null>(null);

  /**
   * pledgeEditModalVisible : 업무서약서 날짜 수정 모달의 표시 여부
   */
  const [pledgeEditModalVisible, setPledgeEditModalVisible] = useState(false);

  /**
   * editingPledgeType : 현재 수정 중인 업무서약서 날짜 유형
   */
  const [editingPledgeType, setEditingPledgeType] = useState<'start' | 'end' | null>(null);

  /**
   * activeTabKey : 현재 활성화된 탭 키
   */
  const [activeTabKey, setActiveTabKey] = useState<string>('1');

  // ============================================================
  // 권한 관리 탭 상태
  // ============================================================

  /**
   * Redux store에서 권한 관리 데이터 가져오기
   */
  const authorizedUsers = useAppSelector((state) => state.Evl0108m.authorizedUsers);
  const allEmployees = useAppSelector((state) => state.Evl0108m.allEmployees);

  /**
   * selectedEmployeeKeys : 전체 직원 목록에서 선택된 직원 키 배열
   */
  const [selectedEmployeeKeys, setSelectedEmployeeKeys] = useState<React.Key[]>([]);

  /**
   * selectedAuthorizedKeys : 권한 보유자 목록에서 선택된 직원 키 배열
   */
  const [selectedAuthorizedKeys, setSelectedAuthorizedKeys] = useState<React.Key[]>([]);

  /**
   * pendingAuthorizedUsers : 로컬에서 임시로 관리하는 권한 보유자 목록 (저장 전 임시 데이터)
   */
  const [pendingAuthorizedUsers, setPendingAuthorizedUsers] = useState<IAuthorizedUserState[]>([]);

  /**
   * hasUnsavedAuthChanges : 권한 관리 탭의 저장되지 않은 변경사항 여부
   */
  const [hasUnsavedAuthChanges, setHasUnsavedAuthChanges] = useState(false);

  /**
   * employeeSearchInput : 전체 직원 목록 검색 입력 필드 값 (입력창의 값)
   */
  const [employeeSearchInput, setEmployeeSearchInput] = useState('');

  /**
   * employeeSearchKeyword : 실제 검색에 사용되는 키워드 (검색 버튼 클릭 시 업데이트)
   */
  const [employeeSearchKeyword, setEmployeeSearchKeyword] = useState('');

  /**
   * employeeCurrentPage : 전체 직원 목록 현재 페이지
   */
  const [employeeCurrentPage, setEmployeeCurrentPage] = useState(1);

  // ============================================================
  // useEffect - Redux 상태 동기화
  // ============================================================

  /**
   * Redux store의 evlCycleList가 변경될 때 로컬 상태 업데이트
   */
  useEffect(() => {
    if (evlCycleList.length > 0) {
      setEvlCycle(evlCycleList[0]);
    }
  }, [evlCycleList]);

  /**
   * Redux store의 authorizedUsers가 변경될 때 pendingAuthorizedUsers 동기화
   */
  useEffect(() => {
    setPendingAuthorizedUsers(authorizedUsers);
    setHasUnsavedAuthChanges(false);
  }, [authorizedUsers]);

  // ============================================================
  // useEffect - 초기 데이터 로드
  // ============================================================

  /**
   * 기능 : componentDidMount (컴포넌트 마운트 시 1회 실행)
   * 설명 : 화면 최초 로드시에 필요한 데이터를 조회한다.
   */
  useEffect(() => {
    // 평가년도 목록 조회 (드롭다운 옵션 생성용)
    loadAvailableYears();

    // 현재 년도의 평가 설정 데이터 조회
    changeYear(evaluationYear);
  }, []);

  /**
   * 기능 : 탭 변경 시 데이터 조회 및 상태 초기화
   * 설명 : 탭이 변경될 때마다 해당 탭의 데이터를 조회하고 양쪽 탭의 상태를 초기화한다.
   */
  useEffect(() => {
    // 평가 일정 관리 탭 상태 초기화
    setEditingPledgeType(null);

    // 권한 관리 탭 상태 초기화
    setSelectedAuthorizedKeys([]);
    setSelectedEmployeeKeys([]);
    setEmployeeSearchInput('');
    setEmployeeSearchKeyword('');
    setEmployeeCurrentPage(1);
    setHasUnsavedAuthChanges(false);

    if (activeTabKey === '1') {
      // 평가 일정 관리 탭 - 연도를 현재 년도로 초기화 후 데이터 조회
      const currentYear = new Date().getFullYear().toString();
      changeYear(currentYear);
    } else if (activeTabKey === '2') {
      // 권한 관리 탭 - 권한 보유자 및 전체 직원 목록 조회
      loadAuthorizedUsers();
      loadAllEmployees();
    }
  }, [activeTabKey]);

  // ============================================================
  // 데이터 계산 (useMemo)
  // ============================================================

  /**
   * filteredEmployees : 검색 키워드로 필터링된 전체 직원 목록
   */
  const filteredEmployees = useMemo(() => {
    if (!employeeSearchKeyword.trim()) {
      return allEmployees;
    }

    return allEmployees.filter(emp =>
      emp.name.toLowerCase().includes(employeeSearchKeyword.toLowerCase())
    );
  }, [allEmployees, employeeSearchKeyword]);

  // ============================================================
  // 이벤트 핸들러
  // ============================================================

  /**
   * 기능 : handleDateChange
   * 설명 : 날짜 변경 이벤트를 처리한다.
   */
  const handleDateChange = (field: keyof IEvlCycleState, date: Dayjs | null) => {
    const selectedYear = parseInt(evaluationYear);
    const currentYear = new Date().getFullYear();

    // 지난 년도 수정 방지
    if (selectedYear < currentYear) {
      message.warning("지난 년도의 일정은 수정할 수 없습니다.");
      return;
    }

    // 날짜 초기화 처리
    if (!date) {
      setEvlCycle((prev) => ({
        ...prev,
        [field]: "",
      }));
      setHasUnsavedChanges(true);
      return;
    }

    const newDate = date.format("YYYY-MM-DD");

    setEvlCycle((prev) => ({
      ...prev,
      [field]: newDate,
    }));

    setHasUnsavedChanges(true);
  };

  /**
   * 기능 : handleSaveEvaluationSchedule
   * 설명 : 평가 일정과 등급 분포 설정을 DB에 저장한다.
   */
  const handleSaveEvaluationSchedule = () => {
    // ===== 1. 날짜 순서 검증 =====
    const startDate       = new Date(evlCycle.hrEvlStDt);
    const selfEvalDate    = new Date(evlCycle.slfEvlEdDt);
    const projectEvalDate = new Date(evlCycle.pjtEvlEdDt);
    const firstEvalDate   = new Date(evlCycle.eval1EdDt);
    const secondEvalDate  = new Date(evlCycle.eval2EdDt);
    const bizPledgeStartDate = new Date(evlCycle.bizPlgStDt);
    const bizPledgeEndDate   = new Date(evlCycle.bizPlgEdDt);

    // 업무서약서 시작일과 종료일은 둘 다 있거나 둘 다 없어야 함
    const hasPledgeStart = evlCycle.bizPlgStDt && evlCycle.bizPlgStDt.trim() !== "";
    const hasPledgeEnd   = evlCycle.bizPlgEdDt && evlCycle.bizPlgEdDt.trim() !== "";

    if (hasPledgeStart && !hasPledgeEnd) {
      message.error("업무서약서 작성 종료일을 입력해주세요.");
      return;
    }

    if (!hasPledgeStart && hasPledgeEnd) {
      message.error("업무서약서 작성 시작일을 입력해주세요.");
      return;
    }

    // 업무서약서 작성 시작일 < 업무서약서 작성 종료일
    if (hasPledgeStart && hasPledgeEnd && bizPledgeStartDate >= bizPledgeEndDate) {
      message.error("업무서약서 작성 시작일은 종료일보다 이전이어야 합니다.");
      return;
    }

    // 인사평가 시작일 < 자기평가 마감일
    if (startDate >= selfEvalDate) {
      message.error("인사평가 시작일은 자기평가 마감일보다 이전이어야 합니다.");
      return;
    }

    // 인사평가 시작일 < 프로젝트평가 마감일
    if (startDate >= projectEvalDate) {
      message.error("인사평가 시작일은 프로젝트평가 마감일보다 이전이어야 합니다.");
      return;
    }

    // 자기평가 마감일 < 1차평가 마감일
    if (selfEvalDate >= firstEvalDate) {
      message.error("자기평가 마감일은 1차평가 마감일보다 이전이어야 합니다.");
      return;
    }

    // 프로젝트평가 마감일 < 1차평가 마감일
    if (projectEvalDate >= firstEvalDate) {
      message.error("프로젝트평가 마감일은 1차평가 마감일보다 이전이어야 합니다.");
      return;
    }

    // 1차평가 마감일 < 2차평가 마감일
    if (firstEvalDate >= secondEvalDate) {
      message.error("1차평가 마감일은 2차평가 마감일보다 이전이어야 합니다.");
      return;
    }


    // ===== 2. 날짜 형식 변환 함수 정의 =====
    const formatDateForDB = (dateStr: string) => {
      if (!dateStr) return "";
      return dateStr.replace(/-/g, "");
    };

    // ===== 3. Redux store에 저장 (YYYY-MM-DD 형식 유지) =====
    const saveData: IEvlCycleState = {
      evlYear    : evaluationYear,
      hrEvlStDt  : evlCycle.hrEvlStDt,
      slfEvlEdDt : evlCycle.slfEvlEdDt,
      pjtEvlEdDt : evlCycle.pjtEvlEdDt,
      eval1EdDt  : evlCycle.eval1EdDt,
      eval2EdDt  : evlCycle.eval2EdDt,
      bizPlgStDt : evlCycle.bizPlgStDt,
      bizPlgEdDt : evlCycle.bizPlgEdDt,
    };

    dispatch(Evl0108mAction.setEvlCycle([saveData]));

    // ===== 5. DB 저장용 데이터 생성 (YYYYMMDD 형식) =====
    const saveDataForDB = {
      evlYear    : evaluationYear,
      hrEvlStDt  : formatDateForDB(evlCycle.hrEvlStDt),
      slfEvlEdDt : formatDateForDB(evlCycle.slfEvlEdDt),
      pjtEvlEdDt : formatDateForDB(evlCycle.pjtEvlEdDt),
      eval1EdDt  : formatDateForDB(evlCycle.eval1EdDt),
      eval2EdDt  : formatDateForDB(evlCycle.eval2EdDt),
      bizPlgStDt : formatDateForDB(evlCycle.bizPlgStDt),
      bizPlgEdDt : formatDateForDB(evlCycle.bizPlgEdDt),
    };

    // ===== 6. 트랜 호출하여 DB 저장 =====
    commonProxy.callAsyncSvc(saveDataForDB, {
      txCd: 'TREVLS0004',
      success: (responseData: any) => {
        // ===== 7. 업무서약서 날짜가 설정된 경우, 전체 직원 대상 업무서약서 레코드 INSERT =====
        if (evlCycle.bizPlgStDt && evlCycle.bizPlgEdDt) {
          const pledgeInsertData = {
            evlYear    : evaluationYear,
            bizPlgStDt : formatDateForDB(evlCycle.bizPlgStDt),
            bizPlgEdDt : formatDateForDB(evlCycle.bizPlgEdDt),
          };

          commonProxy.callAsyncSvc(pledgeInsertData, {
            txCd: 'TREVLS0022',
            success: (pledgeResponse: any) => {
              message.success(`${evaluationYear}년 평가 설정이 저장되었습니다.`);
              setHasUnsavedChanges(false);
            },
            error: (pledgeErrorMsg: string) => {
              message.warning(`평가 설정은 저장되었으나, 업무서약서 직원 데이터 생성에 실패했습니다. (${pledgeErrorMsg})`);
              setHasUnsavedChanges(false);
            }
          });
        } else {
          message.success(`${evaluationYear}년 평가 설정이 저장되었습니다.`);
          setHasUnsavedChanges(false);
        }
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '평가 설정 저장에 실패했습니다.');
      }
    });
  };

  /**
   * 기능 : handleYearChange
   * 설명 : 평가년도 변경 시 저장되지 않은 변경사항을 확인하고 도를 변경한다.
   */
  const handleYearChange = (year: string) => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: '저장되지 않은 변경사항이 있습니다',
        content: '년도를 변경하면 저장되지 않은 변경사항이 사라집니다. 계속하시겠습니까?',
        okText: '계속',
        cancelText: '취소',
        onOk: () => {
          changeYear(year);
        },
      });
    } else {
      changeYear(year);
    }
  };

  /**
   * 기능 : changeYear
   * 설명 : 선택한 년도의 평가 주기 데이터를 DB에서 조회하여 화면에 표시한다.
   */
  const changeYear = (year: string) => {
    // Redux에 조회 조건 저장
    dispatch(Evl0108mAction.setInquiryCnds({ evlYear: year }));
    setHasUnsavedChanges(false);

    const requestData = {
      evlYear: year
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLR0003',
      success: (responseData: any) => {
        const evlCycleItems = responseData.data?.EvlCycleItems || responseData.EvlCycleItems || [];

        if (evlCycleItems.length > 0) {
          const item = evlCycleItems[0];

          const formatDate = (dateStr: string) => {
            if (!dateStr || dateStr.length !== 8) return "";
            return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
          };

          const formattedData: IEvlCycleState = {
            evlYear: item.evlYear || year,
            hrEvlStDt: formatDate(item.hrEvlStDt),
            slfEvlEdDt: formatDate(item.slfEvlEdDt),
            pjtEvlEdDt: formatDate(item.pjtEvlEdDt),
            eval1EdDt: formatDate(item.eval1EdDt),
            eval2EdDt: formatDate(item.eval2EdDt),
            bizPlgStDt: formatDate(item.bizPlgStDt),
            bizPlgEdDt: formatDate(item.bizPlgEdDt),
          };

          setEvlCycle(formattedData);
          dispatch(Evl0108mAction.setEvlCycle([formattedData]));
        } else {
          const emptyData: IEvlCycleState = {
            evlYear: year,
            hrEvlStDt: "",
            slfEvlEdDt: "",
            pjtEvlEdDt: "",
            eval1EdDt: "",
            eval2EdDt: "",
            bizPlgStDt: "",
            bizPlgEdDt: "",
          };

          setEvlCycle(emptyData);
          dispatch(Evl0108mAction.setEvlCycle([emptyData]));
        }
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '평가 설정 조회에 실패했습니다.');
      }
    });
  };

  /**
   * 기능 : loadAvailableYears
   * 설명 : DB에서 평가년도 목록을 조회하여 드롭다운에 표시한다.
   */
  const loadAvailableYears = () => {
    commonProxy.callAsyncSvc({}, {
      txCd: 'TREVLR0013',
      success: (responseData: any) => {

        const evlCycleItems = responseData.data?.EvlCycleItems || responseData.EvlCycleItems || [];
        const years = evlCycleItems.map((item: any) => parseInt(item.evlYear));
        const currentYear = new Date().getFullYear();

        if (years.length > 0) {
          const hasCurrentYear = years.some((year: number) => year === currentYear);

          let finalYears: number[];

          if (hasCurrentYear) {
            finalYears = years;
          } else {
            finalYears = [currentYear, ...years];
          }

          finalYears.sort((a, b) => b - a);

          const yearOptions: IYearOptionState[] = finalYears.map((year: number) => ({
            value: year.toString(),
            label: `${year}년`
          }));

          dispatch(Evl0108mAction.setAvailableYears(yearOptions));

        } else {
          const yearOptions: IYearOptionState[] = [{
            value: currentYear.toString(),
            label: `${currentYear}년`
          }];
          dispatch(Evl0108mAction.setAvailableYears(yearOptions));
        }
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '평가년도 목록 조회에 실패했습니다.');

        const currentYear = new Date().getFullYear();
        const yearOptions: IYearOptionState[] = [{
          value: currentYear.toString(),
          label: `${currentYear}년`
        }];
        dispatch(Evl0108mAction.setAvailableYears(yearOptions));
      }
    });
  };

  /**
   * 기능 : handleEditDeadline
   * 설명 : 평가 마감일 수정 모달을 열고 수정할 평가 유형을 설정한다.
   */
  const handleEditDeadline = (type: keyof IEvlCycleState) => {
    setDeadlineEditModalVisible(true);
    setEditingEvaluationType(type);
    setNewDeadlineDate(evlCycle[type] ? dayjs(evlCycle[type]) : null);
  };

  /**
   * 기능 : handleConfirmDeadlineEdit
   * 설명 : 평가 마감일 수정 모달에서 확인 버튼을 클릭했을 때 실행되는 함수
   */
  const handleConfirmDeadlineEdit = () => {
    if (editingEvaluationType && newDeadlineDate) {
      handleDateChange(editingEvaluationType, newDeadlineDate);
    }
    setDeadlineEditModalVisible(false);
    setEditingEvaluationType(null);
    setNewDeadlineDate(null);
  };

  /**
   * 기능 : handleEditPledge
   * 설명 : 업무서약서 날짜 수정 모달을 열고 수정할 날짜 유형을 설정한다.
   */
  const handleEditPledge = (type: 'start' | 'end') => {
    setPledgeEditModalVisible(true);
    setEditingPledgeType(type);
    setNewDeadlineDate(type === 'start' ? dayjs(evlCycle.bizPlgStDt) : dayjs(evlCycle.bizPlgEdDt));
  };

  /**
   * 기능 : handleConfirmPledgeEdit
   * 설명 : 업무서약서 날짜 수정 모달에서 확인 버튼을 클릭했을 때 실행되는 함수
   */
  const handleConfirmPledgeEdit = () => {
    if (editingPledgeType && newDeadlineDate) {
      const field = editingPledgeType === 'start' ? 'bizPlgStDt' : 'bizPlgEdDt';
      handleDateChange(field, newDeadlineDate);
    }
    setPledgeEditModalVisible(false);
    setEditingPledgeType(null);
    setNewDeadlineDate(null);
  };


  /**
   * 기능 : loadAuthorizedUsers
   * 설명 : 권한 보유자 목록을 DB에서 조회하여 테이블에 표시한다.
   */
  const loadAuthorizedUsers = () => {
    commonProxy.callAsyncSvc({}, {
      txCd: 'TREVLR0023',
      success: (responseData: any) => {
        const users = responseData.data?.EvlAuthorityItems || responseData.EvlAuthorityItems || [];
        const formattedUsers: IAuthorizedUserState[] = users.map((user: any, index: number) => ({
          key: user.empNo || index.toString(),
          userId: user.empNo || '',
          name: user.usrNm || '',
          department: user.usrDpNm || '',
          position: user.usrPosNm || '',
        }));

        dispatch(Evl0108mAction.setAuthorizedUsers(formattedUsers));
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '권한 보유자 조회에 실패했습니다.');
      }
    });
  };

  /**
   * 기능 : loadAllEmployees
   * 설명 : 전체 직원 목록을 DB에서 조회하여 테이블에 표시한다.
   */
  const loadAllEmployees = () => {
    commonProxy.callAsyncSvc({}, {
      txCd: 'TREVLR0022',
      success: (responseData: any) => {
        const employees = responseData.data?.EvlAuthorityItems || responseData.EvlAuthorityItems || [];
        const formattedEmployees: IAllEmployeeState[] = employees.map((emp: any, index: number) => ({
          key: emp.empNo || index.toString(),
          userId: emp.empNo || '',
          name: emp.usrNm || '',
          department: emp.usrDpNm || '',
          position: emp.usrPosNm || '',
        }));

        dispatch(Evl0108mAction.setAllEmployees(formattedEmployees));
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '전체 직원 조회에 실패했습니다.');
      }
    });
  };

  /**
   * 기능 : handleAddUsers
   * 설명 : 전체 직원 목록에서 선택한 직원을 권한 보유자 목록에 추가한다.
   */
  const handleAddUsers = () => {
    if (selectedEmployeeKeys.length === 0) {
      message.warning('추가할 직원을 선택해주세요.');
      return;
    }

    // 선택한 직원들 정보 가져오기
    const selectedEmployees = allEmployees.filter((emp: IAllEmployeeState) => selectedEmployeeKeys.includes(emp.key));

    // 중복 체크: 이미 권한 보유자 목록에 있는 직원 제외
    const newUsers = selectedEmployees.filter((emp: IAllEmployeeState) =>
      !pendingAuthorizedUsers.some((authUser: IAuthorizedUserState) => authUser.key === emp.key)
    );

    if (newUsers.length === 0) {
      message.warning('선택한 직원이 이미 권한 보유자 목록에 있습니다.');
      return;
    }

    // pendingAuthorizedUsers에 추가
    const updatedUsers = [...pendingAuthorizedUsers, ...newUsers];
    setPendingAuthorizedUsers(updatedUsers);
    setHasUnsavedAuthChanges(true);

    // 선택 초기화
    setSelectedEmployeeKeys([]);

    message.success(`${newUsers.length}명을 권한 보유자 목록에 추가했습니다.`);
  };

  /**
   * 기능 : handleRemoveUsers
   * 설명 : 권한 보유자 목록에서 선택한 직원을 삭제한다.
   */
  const handleRemoveUsers = () => {
    if (selectedAuthorizedKeys.length === 0) {
      message.warning('삭제할 권한 보유자를 선택해주세요.');
      return;
    }

    // 선택한 직원들을 제외한 목록 생성
    const updatedUsers = pendingAuthorizedUsers.filter(user =>
      !selectedAuthorizedKeys.includes(user.key)
    );

    setPendingAuthorizedUsers(updatedUsers);
    setHasUnsavedAuthChanges(true);

    // 선택 초기화
    setSelectedAuthorizedKeys([]);

    message.success(`${selectedAuthorizedKeys.length}명을 권한 보유자 목록에서 삭제했습니다.`);
  };

  /**
   * 기능 : handleSaveAuthorizedUsers
   * 설명 : 변경된 권한 보유자 목록을 DB에 저장한다.
   *        기존 권한을 모두 삭제(DELETE)한 후, 현재 목록을 새로 추가(INSERT)한다.
   */
  const handleSaveAuthorizedUsers = () => {
    const saveData = {
      empNoList: pendingAuthorizedUsers.map(user => user.userId)
    };

    commonProxy.callAsyncSvc(saveData, {
      txCd: 'TREVLS0023',
      success: (responseData: any) => {
        message.success('권한 보유자 목록이 저장되었습니다.');
        setHasUnsavedAuthChanges(false);

        // Redux store 업데이트
        dispatch(Evl0108mAction.setAuthorizedUsers(pendingAuthorizedUsers));

        // 권한 보유자 목록 재조회
        loadAuthorizedUsers();
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '권한 보유자 저장에 실패했습니다.');
      }
    });
  };

  /**
   * 기능 : handleEmployeeSearch
   * 설명 : 전체 직원 목록에서 검색을 실행한다.
   */
  const handleEmployeeSearch = () => {
    // 입력된 값을 실제 검색 키워드로 설정
    setEmployeeSearchKeyword(employeeSearchInput);
    // 페이지를 1로 초기화
    setEmployeeCurrentPage(1);
  };

  /**
   * 기능 : handleEmployeeSearchReset
   * 설명 : 전체 직원 목록 검색을 초기화한다.
   */
  const handleEmployeeSearchReset = () => {
    setEmployeeSearchInput('');
    setEmployeeSearchKeyword('');
    setEmployeeCurrentPage(1);
  };

  /**
   * 기능 : handleTabChange
   * 설명 : 탭 변경 시 미저장 내역이 있으면 경고창을 표시한다.
   */
  const handleTabChange = (activeKey: string) => {
    // 변경사항이 있는지 확인
    const hasChanges = hasUnsavedChanges || hasUnsavedAuthChanges;

    if (hasChanges) {
      Modal.confirm({
        title: '변경사항이 있습니다',
        content: '저장하지 않은 내역이 사라집니다. 계속하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onOk: () => {
          setActiveTabKey(activeKey);
        },
      });
    } else {
      setActiveTabKey(activeKey);
    }
  };

  // ============================================================
  // 테이블 컬럼 정의
  // ============================================================

  /**
   * 권한 보유자 테이블 컬럼 정의
   */
  const authorizedUsersColumns: TableColumnsType<IAuthorizedUserState> = [
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      align: 'center',
    },
    {
      title: '부서',
      dataIndex: 'department',
      key: 'department',
      width: '50%',
      align: 'center',
    },
    {
      title: '직급',
      dataIndex: 'position',
      key: 'position',
      width: '25%',
      align: 'center',
    },
  ];

  /**
   * 전체 직원 테이블 컬럼 정의
   */
  const allEmployeesColumns: TableColumnsType<IAllEmployeeState> = [
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      align: 'center',
    },
    {
      title: '부서',
      dataIndex: 'department',
      key: 'department',
      width: '50%',
      align: 'center',
    },
    {
      title: '직급',
      dataIndex: 'position',
      key: 'position',
      width: '25%',
      align: 'center',
    },
  ];

  // ============================================================
  // 탭 아이템 정의
  // ============================================================

  /**
   * 평가 일정 관리 탭 콘텐츠
   */
  const renderScheduleTab = () => (
    <div>
      {/* 전체 컨테이너 */}
      <Space direction="vertical" size="small" style={{ width: '100%' }}>

      {/* ========== 평가년도 선택 및 저장 버튼 ========== */}
      <div style={{ marginBottom: 8, padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
        <Row gutter={16} align="middle">
          {/* 평가년도 드롭다운 */}
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>평가년도</Text>
              <Select
                value={evaluationYear}
                onChange={handleYearChange}
                style={{ width: '100%' }}
                options={availableYears}
              />
            </Space>
          </Col>
          {/* 설정 저장 버튼 */}
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              size="large"
              onClick={handleSaveEvaluationSchedule}
              disabled={!hasUnsavedChanges}
            >
              설정 저장
            </Button>
          </Col>
        </Row>
      </div>

      {/* ========== 업무서약서 작성 기간 설정 섹션 ========== */}
      <div style={{ marginBottom: 12, padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
        {/* 섹션 헤더 */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
          <span style={{ fontWeight: 500, fontSize: 16 }}>업무서약서 작성 기간 설정</span>
          <Text type="secondary" style={{ fontSize: 12 }}>날짜를 클릭하여 변경</Text>
        </div>
        {/* 카드 그리드 */}
        <div>
          <Row gutter={[16, 16]}>
            {/* 업무서약서 작성 시작일 카드 */}
            <Col xs={24} sm={12} md={12}>
              <div
                style={{
                  borderLeft: '4px solid #13c2c2',
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>업무서약서 작성 시작일</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜를 선택하세요"
                    value={evlCycle.bizPlgStDt ? dayjs(evlCycle.bizPlgStDt) : null}
                    onChange={(date) => handleDateChange('bizPlgStDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                    allowClear
                  />
                </Space>
              </div>
            </Col>

            {/* 업무서약서 작성 종료일 카드 */}
            <Col xs={24} sm={12} md={12}>
              <div
                style={{
                  borderLeft: '4px solid #13c2c2',
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>업무서약서 작성 종료일</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜를 선택하세요"
                    value={evlCycle.bizPlgEdDt ? dayjs(evlCycle.bizPlgEdDt) : null}
                    onChange={(date) => handleDateChange('bizPlgEdDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                    allowClear
                  />
                </Space>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* ========== 평가 마감일 설정 섹션 ========== */}
      <div style={{ marginBottom: 12, padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
        {/* 섹션 헤더 */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
          <span style={{ fontWeight: 500, fontSize: 16 }}>평가 마감일 설정</span>
          <Text type="secondary" style={{ fontSize: 12 }}>날짜를 클릭하여 변경</Text>
        </div>
        {/* 5개 카드 그리드 */}
        <div>
          <Row gutter={[16, 16]}>
            {/* 인사평가 시작일 카드 */}
            <Col xs={24} sm={12} lg={{ span: 24/5 }} style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <div
                style={{
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>인사평가 시작일</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜 선택"
                    value={evlCycle.hrEvlStDt ? dayjs(evlCycle.hrEvlStDt) : null}
                    onChange={(date) => handleDateChange('hrEvlStDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                    allowClear
                  />
                </Space>
              </div>
            </Col>

            {/* 자기평가 마감일 카드 */}
            <Col xs={24} sm={12} lg={{ span: 24/5 }} style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <div
                style={{
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>자기평가 마감</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜 선택"
                    value={evlCycle.slfEvlEdDt ? dayjs(evlCycle.slfEvlEdDt) : null}
                    onChange={(date) => handleDateChange('slfEvlEdDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                    allowClear
                  />
                </Space>
              </div>
            </Col>

            {/* 프로젝트평가 마감일 카드 */}
            <Col xs={24} sm={12} lg={{ span: 24/5 }} style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <div
                style={{
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>프로젝트평가 마감</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜 선택"
                    value={evlCycle.pjtEvlEdDt ? dayjs(evlCycle.pjtEvlEdDt) : null}
                    onChange={(date) => handleDateChange('pjtEvlEdDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                  />
                </Space>
              </div>
            </Col>

            {/* 1차평가 마감일 카드 */}
            <Col xs={24} sm={12} lg={{ span: 24/5 }} style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <div
                style={{
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>1차평가 마감</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜 선택"
                    value={evlCycle.eval1EdDt ? dayjs(evlCycle.eval1EdDt) : null}
                    onChange={(date) => handleDateChange('eval1EdDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                  />
                </Space>
              </div>
            </Col>

            {/* 2차평가 마일 카드 */}
            <Col xs={24} sm={12} lg={{ span: 24/5 }} style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <div
                style={{
                  padding: '16px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>2차평가 마감</Text>
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="날짜 선택"
                    value={evlCycle.eval2EdDt ? dayjs(evlCycle.eval2EdDt) : null}
                    onChange={(date) => handleDateChange('eval2EdDt', date)}
                    format="YYYY-MM-DD"
                    disabled={parseInt(evaluationYear) < new Date().getFullYear()}
                    allowClear
                  />
                </Space>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Space>
    </div>
  );

  /**
   * 권한 관리 탭 콘텐츠
   */
  const renderAuthorityTab = () => {
    // 현재 로그인한 사용자의 사번 가져오기
    const currentUserEmpNo = common.getEmpNo();

    return (
    <div>
      {/* 전체 컨테이너 */}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* ========== 저장 버튼 영역 ========== */}
        <div style={{ padding: '16px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
          <Row gutter={16} align="middle">
            <Col xs={12}>
              <div style={{ fontSize: '14px', color: '#595959', lineHeight: '1.5' }}>
                <div>전 직원 평가를 관리할 수 있는 인사평가 권한을 지정합니다.</div>
                <div>로그인 당사자의 권한은 삭제할 수 없습니다.</div>
              </div>
            </Col>
            <Col xs={12} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                size="large"
                onClick={handleSaveAuthorizedUsers}
                disabled={!hasUnsavedAuthChanges}
              >
                권한 보유자 저장
              </Button>
            </Col>
          </Row>
        </div>

        {/* ========== 권한 보유자 목록 ========== */}
        <div style={{ padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
            <span style={{ fontWeight: 500, fontSize: 16 }}>권한 보유자 목록</span>
            <Text type="secondary" style={{ fontSize: 12 }}>평가 권한을 가진 인원을 조회합니다.</Text>
          </div>

          {/* 삭제 버튼 */}
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveUsers}
              disabled={selectedAuthorizedKeys.length === 0}
            >
              선택 삭제 ({selectedAuthorizedKeys.length})
            </Button>
          </div>

          <Table
            rowSelection={{
              selectedRowKeys: selectedAuthorizedKeys,
              onChange: (selectedRowKeys) => setSelectedAuthorizedKeys(selectedRowKeys),
              getCheckboxProps: (record) => ({
                disabled: record.userId === currentUserEmpNo,
              }),
            }}
            columns={authorizedUsersColumns}
            dataSource={pendingAuthorizedUsers}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `전체 ${total}명`, 
            }}
            bordered
            size="middle"
          />
        </div>

        {/* ========== 전체 직원 목록 ========== */}
        <div style={{ padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
            <span style={{ fontWeight: 500, fontSize: 16 }}>전체 직원 목록</span>
            <Text type="secondary" style={{ fontSize: 12 }}>전체 직원 정보를 조회합니다.</Text>
          </div>

          {/* 추가 버튼 및 검색 */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddUsers}
                disabled={selectedEmployeeKeys.length === 0}
              >
                권한 추가 ({selectedEmployeeKeys.length})
              </Button>
              <Input
                placeholder="이름으로 검색"
                value={employeeSearchInput}
                onChange={(e) => setEmployeeSearchInput(e.target.value)}
                onPressEnter={handleEmployeeSearch}
                style={{ width: 300 }}
              />
              <Button type="primary" onClick={handleEmployeeSearch}>검색</Button>
              <Button onClick={handleEmployeeSearchReset}>초기화</Button>
            </Space>
            <Button
              onClick={() => setSelectedEmployeeKeys([])}
              disabled={selectedEmployeeKeys.length === 0}
            >
              선택 초기화
            </Button>
          </div>

          <Table
            rowSelection={{
              selectedRowKeys: selectedEmployeeKeys,
              onChange: (selectedRowKeys) => setSelectedEmployeeKeys(selectedRowKeys),
              preserveSelectedRowKeys: true,
            }}
            columns={allEmployeesColumns}
            dataSource={filteredEmployees}
            pagination={{
              current: employeeCurrentPage,
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `전체 ${total}명`,
              onChange: (page) => setEmployeeCurrentPage(page),
            }}
            bordered
            size="middle"
          />
        </div>
      </Space>
    </div>
    );
  };

  /**
   * 탭 아이템 정의
   */
  const tabItems: TabsProps['items'] = useMemo(() => [
    {
      key: '1',
      label: '평가 일정 관리',
      children: renderScheduleTab(),
    },
    {
      key: '2',
      label: '권한 관리',
      children: renderAuthorityTab(),
    },
  ], [evlCycle, pendingAuthorizedUsers, allEmployees, selectedAuthorizedKeys, selectedEmployeeKeys, hasUnsavedAuthChanges, employeeSearchInput, employeeSearchKeyword, employeeCurrentPage]);

  // ============================================================
  // 메인 렌더링
  // ============================================================

  return (
    <div>
      {/* 전체 컨테이너 */}
      <Space direction="vertical" size="small" style={{ width: '100%', padding: 24 }}>
        {/* 페이지 제목 */}
        <div>
          <h1 style={{ textAlign: 'center', marginBottom: 16 }}>
            평가 정보 관리
          </h1>
        </div>

        {/* ========== 탭 메뉴 ========== */}
        <Tabs
          activeKey={activeTabKey}
          onChange={handleTabChange}
          items={tabItems}
          size="large"
          style={{ marginTop: 16 }}
        />


        {/* ========== 평가 마감일 수정 모달 ========== */}
        <Modal
          title="평가 기한 수정"
          open={deadlineEditModalVisible}
          onCancel={() => {
            setDeadlineEditModalVisible(false);
            setEditingEvaluationType(null);
            setNewDeadlineDate(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setDeadlineEditModalVisible(false);
                setEditingEvaluationType(null);
                setNewDeadlineDate(null);
              }}
            >
              취소
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={handleConfirmDeadlineEdit}
            >
              확정
            </Button>,
          ]}
        >
          {editingEvaluationType && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <Text strong>평가 유형: </Text>
                <Text>
                  {editingEvaluationType === "hrEvlStDt" ? "인사평가 시작일" :
                   editingEvaluationType === "slfEvlEdDt" ? "자기평가" :
                   editingEvaluationType === "pjtEvlEdDt" ? "프로젝트평가" :
                   editingEvaluationType === "eval1EdDt" ? "1차평가" :
                   editingEvaluationType === "eval2EdDt" ? "2차평가" :
                   "알 수 없음"}
                </Text>
              </div>
              <div>
                <Text strong>현재 마감일: </Text>
                <Text>
                  {evlCycle[editingEvaluationType] || '미설정'}
                </Text>
              </div>

              <div>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>새로운 마감일 선택</Text>
                </div>
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="새 마감일 선택"
                  value={newDeadlineDate}
                  onChange={(date) => setNewDeadlineDate(date)}
                  disabledDate={(current) => {
                    if (!current) return false;

                    const currentDate = current.toDate();
                    const selectedYear = parseInt(evaluationYear);
                    const currentYear = new Date().getFullYear();

                    if (selectedYear < currentYear) {
                      return true;
                    }

                    return false;
                  }}
                  format="YYYY-MM-DD"
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">
                    {evaluationYear && parseInt(evaluationYear) < new Date().getFullYear()
                      ? "* 지난 년도의 날짜는 수정할 수 없습니다."
                      : "* 모든 날짜를 선택할 수 있습니다."}
                  </Text>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* ========== 업무서약서 날짜 수정 모달 ========== */}
        <Modal
          title="업무서약서 날짜 수정"
          open={pledgeEditModalVisible}
          onCancel={() => {
            setPledgeEditModalVisible(false);
            setEditingPledgeType(null);
            setNewDeadlineDate(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setPledgeEditModalVisible(false);
                setEditingPledgeType(null);
                setNewDeadlineDate(null);
              }}
            >
              취소
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={handleConfirmPledgeEdit}
            >
              확정
            </Button>,
          ]}
        >
          {editingPledgeType && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <Text strong>업무서약서 날짜 유형: </Text>
                <Text>
                  {editingPledgeType === "start" ? "작성 시작일" :
                   editingPledgeType === "end" ? "작성 종료일" :
                   "알 수 없음"}
                </Text>
              </div>
              <div>
                <Text strong>현재 날짜: </Text>
                <Text>
                  {editingPledgeType === "start" ? evlCycle.bizPlgStDt || '미설정' :
                   editingPledgeType === "end" ? evlCycle.bizPlgEdDt || '미설정' :
                   "알 수 없음"}
                </Text>
              </div>

              <div>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>새로운 날짜 선택</Text>
                </div>
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="새 날짜 선택"
                  value={newDeadlineDate}
                  onChange={(date) => setNewDeadlineDate(date)}
                  disabledDate={(current) => {
                    if (!current) return false;

                    const currentDate = current.toDate();
                    const selectedYear = parseInt(evaluationYear);
                    const currentYear = new Date().getFullYear();

                    if (selectedYear < currentYear) {
                      return true;
                    }

                    return false;
                  }}
                  format="YYYY-MM-DD"
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">
                    {evaluationYear && parseInt(evaluationYear) < new Date().getFullYear()
                      ? "* 지난 년도의 날짜는 수정할 수 없습니다."
                      : "* 모든 날짜를 선택할 수 있습니다."}
                  </Text>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </Space>
    </div>
  );
};

export default Evl0108m;